/**
 * A bank account entered by routing and account number is verified by Stripe's micro-deposits,
 * not on the spot. These are the pure pieces of that path: what a confirmed SetupIntent says
 * about it, the row the form writes for it, and the words the toast and the Billing card use.
 * Pure, so they can be proved without mounting Stripe's Payment Element.
 */
import { toPaymentMethodRow, type SavedPaymentMethodDetails } from './paymentMethodRow';

/**
 * How Stripe has the dealer prove the account: two deposits whose amounts they enter, or one
 * deposit carrying a 6-character code (starting "SM") in its bank statement description.
 */
export type MicrodepositType = 'amounts' | 'descriptor_code';

/** The part of stripe.confirmSetup's SetupIntent this module reads; stripe-js types these loosely. */
export interface ConfirmedSetupIntent {
  id: string;
  status: string;
  next_action?: { type: string; verify_with_microdeposits?: { microdeposit_type?: string | null } | null } | null;
}

export interface PendingBankSetup {
  setupIntentId: string;
  microdepositType: MicrodepositType | null;
}

/** When a confirmed bank SetupIntent is waiting on micro-deposits, which one and of what kind; null when it is not. */
export const pendingBankSetup = (setupIntent: ConfirmedSetupIntent | null | undefined): PendingBankSetup | null => {
  if (!setupIntent || setupIntent.status !== 'requires_action') return null;
  if (setupIntent.next_action?.type !== 'verify_with_microdeposits') return null;
  const type = setupIntent.next_action.verify_with_microdeposits?.microdeposit_type;
  return { setupIntentId: setupIntent.id, microdepositType: type === 'amounts' || type === 'descriptor_code' ? type : null };
};

/**
 * The row for a bank account still waiting on micro-deposits: never primary (the database refuses
 * a pending primary, and autopay charges the primary), with the SetupIntent to ask Stripe about.
 */
export const pendingBankRow = (details: SavedPaymentMethodDetails, setupIntentId: string) => ({
  ...toPaymentMethodRow(details),
  primary: false,
  pendingSetupIntentId: setupIntentId,
});

/** What Stripe is sending, in the dealer's terms. */
export const microdepositsSentence = (type: MicrodepositType | null | undefined): string => {
  if (type === 'amounts') return 'Stripe is sending two small deposits to this account (1–2 business days).';
  if (type === 'descriptor_code') {
    return 'Stripe is sending one small deposit to this account (1–2 business days), with a 6-character code starting with SM in its description.';
  }
  return 'Stripe is sending a small deposit or two to this account (1–2 business days).';
};

/** What the dealer does with it, completing "choose Verify and …". */
export const microdepositEntry = (type: MicrodepositType | null | undefined): string => {
  if (type === 'amounts') return 'enter the two amounts';
  if (type === 'descriptor_code') return 'enter that code';
  return 'follow Stripe’s instructions';
};
